'use client';

import dynamic from 'next/dynamic';
import { V4_BRAND } from '@/lib/marketing/messaging-bank';

// Floating Voice v1 is the canonical voice interaction shell.
const FloatingVoiceV1 = dynamic(
  () => import('@/components/voice/FloatingVoiceV1'),
  { ssr: false },
);

/**
 * Ambient atmosphere layer — CSS-only glows, rotating rings, and particles.
 * No Three.js needed: the VoiceRingCTA "Tap to Talk" label created a confusing
 * artifact since this page has its own phone widget as the interactive element.
 * Pure CSS is lighter, loads instantly, and avoids the 120KB Three.js chunk.
 */
export function HeroAmbientLayer() {
  return (
    <div className="hero-ambient-layer" aria-hidden="true">
      {/* Primary cyan glow — centered behind the phone (right column) */}
      <div
        style={{
          position: 'absolute',
          right: '12%',
          top: '45%',
          transform: 'translate(0, -50%)',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(125,250,255,0.10) 0%, rgba(34,211,238,0.04) 35%, transparent 65%)',
          pointerEvents: 'none',
          filter: 'blur(40px)',
        }}
      />
      {/* Secondary warm glow — bottom-left atmospheric fill */}
      <div
        style={{
          position: 'absolute',
          left: '-5%',
          bottom: '-10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,213,146,0.04) 0%, transparent 60%)',
          pointerEvents: 'none',
          filter: 'blur(60px)',
        }}
      />
      {/* Rotating ambient rings — slow, subtle, geometric */}
      <div
        className="hero-ambient-ring"
        style={{
          position: 'absolute',
          right: '8%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 550,
          height: 550,
          borderRadius: '50%',
          border: '1px solid rgba(125,250,255,0.04)',
          animation: 'hero-ring-rotate 20s linear infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        className="hero-ambient-ring"
        style={{
          position: 'absolute',
          right: '14%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 380,
          height: 380,
          borderRadius: '50%',
          border: '1px solid rgba(52,211,153,0.03)',
          animation: 'hero-ring-rotate 14s linear infinite reverse',
          pointerEvents: 'none',
        }}
      />
      {/* Floating particles (CSS-only) */}
      <span className="hero-particle" style={{ right: '30%', top: '20%', background: 'rgba(125,250,255,0.35)', animationDelay: '0s' }} />
      <span className="hero-particle" style={{ right: '48%', top: '68%', background: 'rgba(255,213,146,0.25)', animationDelay: '1.8s' }} />
      <span className="hero-particle" style={{ right: '18%', top: '78%', background: 'rgba(185,246,192,0.3)', animationDelay: '3.2s' }} />
      <span className="hero-particle" style={{ right: '58%', top: '15%', background: 'rgba(125,250,255,0.2)', animationDelay: '4.8s' }} />
      <span className="hero-particle" style={{ right: '8%', top: '35%', background: 'rgba(255,213,146,0.2)', animationDelay: '2.4s' }} />
    </div>
  );
}

/** Interactive phone widget — renders inside server-rendered skeleton wrapper. */
export function HeroPhoneWidget() {
  return (
    <div
      className="hero-phone-preview hero-phone-preview--floating-voice hover:scale-[1.02]"
      style={{
        position: 'relative',
        display: 'block',
        width: 240,
        height: 456,
        borderRadius: 42,
        padding: 0,
        margin: '0 auto',
        background: 'linear-gradient(180deg, #191d27 0%, #0b0d13 100%)',
        boxShadow:
          'inset 0 0 0 2px #2a2d38, inset 0 0 0 7px #1a1d26, 0 60px 100px -30px rgba(0,0,0,0.85), 0 0 80px -20px rgba(125,250,255,0.28)',
        transition: 'transform 300ms cubic-bezier(0.2,0.9,0.3,1)',
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 78,
          height: 6,
          borderRadius: 999,
          background: '#05060a',
          zIndex: 2,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: '42px 18px 18px',
          display: 'grid',
          placeItems: 'center',
          borderRadius: 28,
          background:
            'radial-gradient(circle at 50% 28%, rgba(125,250,255,0.18), transparent 34%), linear-gradient(180deg, rgba(244,244,240,0.06), rgba(244,244,240,0.015))',
        }}
      >
        <FloatingVoiceV1
          surface="homepage"
          variant="centered-ring"
          size={178}
          helperText={{
            idle: 'Tap for demo',
            connecting: 'Connecting...',
            listening: 'Bri is listening.',
            speaking: 'Bri is speaking.',
            ended: 'Call ended. Tap to talk again.',
          }}
          postCall={{ mode: 'cta', href: V4_BRAND.postDemoCtaHref, label: 'Get started' }}
          testId="ai-receptionist-floating-voice-v1"
        />
      </div>
    </div>
  );
}
